import type {
  CarryState,
  CoverageReport,
  EngineInput,
  EngineResult,
  JetLayout,
  PublishedRow,
  ScheduleRow,
  Seat,
  SeatCandidates,
  ValidationReport,
  WindowInfo,
} from '@conciair/scheduler-core';
import type { OpName, WorkerRequest, WorkerResponse } from './worker';

/**
 * Promise API over the engine worker. One worker for the whole app; calls
 * are serialized by id so several pages can use it at once.
 */
class EngineClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private pending = new Map<number, { resolve(v: unknown): void; reject(e: Error): void }>();

  private get w(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'scheduler-core' });
      this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
        const msg = e.data;
        const p = this.pending.get(msg.id);
        if (!p) return;
        this.pending.delete(msg.id);
        if (msg.ok) p.resolve({ result: msg.result, ms: msg.ms });
        else p.reject(new Error(msg.error));
      };
      this.worker.onerror = (ev) => {
        const err = new Error('Engine worker failed: ' + (ev.message || 'unknown error'));
        for (const p of this.pending.values()) p.reject(err);
        this.pending.clear();
        this.worker = null;
      };
    }
    return this.worker;
  }

  private call<T>(op: OpName, ...args: unknown[]): Promise<{ result: T; ms: number }> {
    const id = this.nextId++;
    const req: WorkerRequest = { id, op, args };
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.w.postMessage(req);
    });
  }

  async version(): Promise<{ engineVersion: string; coreHash: string }> {
    return (await this.call<{ engineVersion: string; coreHash: string }>('version')).result;
  }
  async validate(input: EngineInput): Promise<ValidationReport> {
    return (await this.call<ValidationReport>('validate', input)).result;
  }
  async coverage(input: EngineInput): Promise<CoverageReport> {
    return (await this.call<CoverageReport>('coverage', input)).result;
  }
  /** Returns the result and the wall-clock time the engine took (stored on the run). */
  async generate(input: EngineInput): Promise<{ result: EngineResult; ms: number }> {
    return this.call<EngineResult>('generate', input);
  }
  async jetLayout(input: EngineInput, schedule: ScheduleRow[]): Promise<JetLayout | null> {
    return (await this.call<JetLayout | null>('jetLayout', input, schedule)).result;
  }
  async rebuildCarry(input: EngineInput, published: PublishedRow[]): Promise<CarryState> {
    return (await this.call<CarryState>('rebuildCarry', input, published)).result;
  }
  async validStarts(input: EngineInput, type: string): Promise<string[]> {
    return (await this.call<string[]>('validStarts', input, type)).result;
  }
  async windows(input: EngineInput): Promise<WindowInfo[]> {
    return (await this.call<WindowInfo[]>('windows', input)).result;
  }
  async seatCandidates(input: EngineInput, schedule: ScheduleRow[], tail: string, rotationStart: string, seat: Seat): Promise<SeatCandidates> {
    return (await this.call<SeatCandidates>('seatCandidates', input, schedule, tail, rotationStart, seat)).result;
  }
  async amendCarry(carryOut: CarryState, carryIn: CarryState, before: ScheduleRow[], after: ScheduleRow[]): Promise<CarryState> {
    return (await this.call<CarryState>('amendCarry', carryOut, carryIn, before, after)).result;
  }
}

export const engine = new EngineClient();
